import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import { toast } from 'sonner';
import { countryCodes } from '../utils/countryCodes';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [countryCode, setCountryCode] = useState('+1');
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [isLoadingGeo, setIsLoadingGeo] = useState(false);

  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  // Fetch geolocation on component mount
  useEffect(() => {
    fetchGeoLocation();
  }, []);

  const fetchGeoLocation = async () => {
    setIsLoadingGeo(true);
    try {
      const countryInfo = await getCountryInfo();
      if (countryInfo) {
        setDetectedCountry(countryInfo);
        console.log('Detected country:', countryInfo);
        
        // Auto-set country code based on detected country
        const matchedCountry = countryCodes.find(c => c.country === countryInfo.countryCode);
        if (matchedCountry) {
          setCountryCode(matchedCountry.code);
          console.log('Auto-set country code to:', matchedCountry.code, matchedCountry.name);
        }
      }
    } catch (error) {
      console.error('Failed to fetch geolocation:', error);
    } finally {
      setIsLoadingGeo(false);
    }
  };

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation
  const validatePhone = (phone) => {
    // Validate based on country code
    const fullPhone = countryCode + ' ' + phone;
    const phoneRegex = /^\+\d{1,3}[\s\-\.]?\(?\d{3}\)?[\s\-\.]?\d{3}[\s\-\.]?\d{4}$/;
    return phoneRegex.test(fullPhone) && phone.replace(/[^\d]/g, '').length >= 10;
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneBlur = () => {
    if (formData.phone && !validatePhone(formData.phone)) {
      setErrors(prev => ({ ...prev, phone: 'Please enter a valid 10-digit phone number' }));
    } else {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate before submission
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
      hasErrors = true;
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsLoading(true);
    
    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        countryCode: countryCode,
        country: countryCodes.find(c => c.code === countryCode)?.country || 'US',
        description: formData.message
      };

      const result = await submitLeadWithGeoLocation(leadData, null, 'Contact Us Page');
      
      if (result.success) {
        toast.success(result.message || 'Message sent successfully!');
        setFormData({ name: '', email: '', phone: '', message: '' });
        // API will handle redirect automatically
      } else {
        toast.error(result.message || 'Failed to send message. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again later.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#012169] via-[#8B1587] to-[#6A0572] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-[#F1F1F1] mb-6">Contact Us</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Have questions about protecting your intellectual property? Our team is here to help.
          </p>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FFD700] to-[#F97316] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#012169] mb-2">Phone</h3>
                    <p className="text-gray-600">+1 (646) 687-2501</p>
                    <p className="text-sm text-gray-500 mt-1">Mon-Fri, 9AM-6PM EST</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FFD700] to-[#F97316] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#012169] mb-2">Email</h3>
                    <p className="text-gray-600">info@crystaltrademark.com</p>
                    <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FFD700] to-[#F97316] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#012169] mb-2">Office</h3>
                    <p className="text-gray-600">
                      765 Market St APT 34D<br />
                      San Francisco, CA 94103
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FFD700] to-[#F97316] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#012169] mb-2">Business Hours</h3>
                    <p className="text-gray-600">
                      Monday - Friday: 9AM - 6PM<br />
                      Saturday: 10AM - 4PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="p-8">
                <h2 className="text-3xl font-bold text-[#012169] mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input
                        type="text"
                        placeholder="Full Name *"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Email Address *"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        onBlur={handleEmailBlur}
                        className={errors.email ? 'border-red-500' : ''}
                        required
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                      {detectedCountry && (
                        <span className="ml-2 text-xs text-gray-500 font-normal">
                          (Detected: {detectedCountry.countryName})
                        </span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="w-32 px-3 py-2 border-2 border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#6A0572] focus:border-[#6A0572] cursor-pointer transition-all"
                      >
                        {countryCodes.map((country, index) => (
                          <option key={index} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <Input
                        type="tel"
                        placeholder="123-456-7890"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^\d\s\-]/g, '');
                          setFormData({...formData, phone: value});
                        }}
                        onBlur={handlePhoneBlur}
                        className={`flex-1 border-2 ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:border-[#6A0572] focus:ring-2 focus:ring-[#6A0572]/20`}
                        required
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1 flex items-start gap-1">
                        <span>⚠️</span>
                        <span>{errors.phone}</span>
                      </p>
                    )}
                  </div>
                  <Textarea
                    placeholder="Your Message *"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    rows={6}
                  />
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] text-[#012169] font-bold text-lg py-6"
                  >
                    {isLoading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-300 rounded-2xl overflow-hidden h-96 flex items-center justify-center">
            <p className="text-gray-600 text-lg">Map Integration Placeholder</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;